import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Sidebar from '../components/Sidebar';
import ThemeToggle from '../components/ThemeToggle';
import { Users, UserPlus, Trash2, Shield, User, TrendingUp, Calendar, DollarSign } from 'lucide-react';
import './Events.css';

function ManageClient() {
  const { clientId } = useParams();
  const navigate = useNavigate();
  
  const [client, setClient] = useState(null);
  const [teamMembers, setTeamMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingTeam, setLoadingTeam] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  
  // Add team member modal
  const [showAddModal, setShowAddModal] = useState(false);
  const [newMemberEmail, setNewMemberEmail] = useState('');
  const [newMemberFirstName, setNewMemberFirstName] = useState('');
  const [newMemberLastName, setNewMemberLastName] = useState('');
  const [newMemberPassword, setNewMemberPassword] = useState('');
  const [newMemberRole, setNewMemberRole] = useState('member');
  const [addingMember, setAddingMember] = useState(false);
  
  // Plan management
  const [showPlanModal, setShowPlanModal] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState('');
  const [updatingPlan, setUpdatingPlan] = useState(false);

  useEffect(() => {
    const stored = localStorage.getItem('user');
    if (!stored) {
      navigate('/admin/login');
      return;
    }
    
    const data = JSON.parse(stored);
    setIsSuperAdmin(data.role === 'super_admin');
    
    if (data.role !== 'super_admin') {
      navigate('/');
      return;
    }
    
    loadClient();
    loadTeamMembers();
  }, [clientId, navigate]);

  const loadClient = async () => {
    setLoading(true);
    try {
      const token = sessionStorage.getItem('adminToken') || localStorage.getItem('token');
      const response = await fetch('https://api.eventpiclive.com/api/admin/clients', {
        headers: {
          'Authorization': `Bearer ${token}`
        },
        credentials: 'include'
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch client');
      }
      
      const data = await response.json();
      const foundClient = data.clients.find(c => c.id === clientId);
      
      if (!foundClient) {
        alert('Client not found');
        navigate('/admin/clients');
        return;
      }
      
      setClient(foundClient);
      setSelectedPlan(foundClient.subscription_tier || 'Starter');
    } catch (error) {
      console.error('Error loading client:', error);
      alert('Failed to load client');
    } finally {
      setLoading(false);
    }
  };

  const loadTeamMembers = async () => {
    setLoadingTeam(true);
    try {
      const token = sessionStorage.getItem('adminToken') || localStorage.getItem('token');
      const response = await fetch(`https://api.eventpiclive.com/api/admin/clients/${clientId}/team`, {
        headers: {
          'Authorization': `Bearer ${token}`
        },
        credentials: 'include'
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch team members');
      }
      
      const data = await response.json();
      setTeamMembers(data.admins || []);
    } catch (error) {
      console.error('Error fetching team:', error);
    } finally {
      setLoadingTeam(false);
    }
  };

  const handleAddTeamMember = async () => {
    if (!newMemberEmail || !newMemberFirstName || !newMemberLastName || !newMemberPassword) {
      alert('Please fill in all fields');
      return;
    }
    
    setAddingMember(true);
    try {
      const token = sessionStorage.getItem('adminToken') || localStorage.getItem('token');
      const response = await fetch(`https://api.eventpiclive.com/api/admin/clients/${clientId}/team`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({
          email: newMemberEmail,
          firstName: newMemberFirstName,
          lastName: newMemberLastName,
          password: newMemberPassword,
          role: newMemberRole
        })
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to add team member');
      }
      
      alert('Team member added successfully!');
      setShowAddModal(false);
      setNewMemberEmail('');
      setNewMemberFirstName('');
      setNewMemberLastName('');
      setNewMemberPassword('');
      setNewMemberRole('member');
      
      loadTeamMembers();
    } catch (error) {
      console.error('Error adding team member:', error);
      alert(error.message);
    } finally {
      setAddingMember(false);
    }
  };

  const handleRemoveTeamMember = async (adminId, adminName) => {
    if (!window.confirm(`Remove ${adminName} from this client's team?`)) {
      return;
    }
    
    try {
      const token = sessionStorage.getItem('adminToken') || localStorage.getItem('token');
      const response = await fetch(`https://api.eventpiclive.com/api/admin/clients/${clientId}/team/${adminId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        credentials: 'include'
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to remove team member');
      }
      
      alert('Team member removed successfully!');
      loadTeamMembers();
    } catch (error) {
      console.error('Error removing team member:', error);
      alert(error.message);
    }
  };

  const handleUpdatePlan = async () => {
    if (!selectedPlan) {
      alert('Please select a plan');
      return;
    }
    
    setUpdatingPlan(true);
    try {
      const token = sessionStorage.getItem('adminToken') || localStorage.getItem('token');
      const response = await fetch(`https://api.eventpiclive.com/api/admin/clients/${clientId}/plan`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({
          plan: selectedPlan
        })
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to update plan');
      }
      
      alert('Plan updated successfully!');
      setShowPlanModal(false);
      loadClient();
    } catch (error) {
      console.error('Error updating plan:', error);
      alert(error.message);
    } finally {
      setUpdatingPlan(false);
    }
  };

  const handleLogout = () => {
    localStorage.clear();
    sessionStorage.clear();
    navigate('/admin/login');
  };

  if (loading) {
    return (
      <div className={`dashboard-layout ${sidebarCollapsed ? 'sidebar-collapsed' : ''}`}>
        <Sidebar 
          collapsed={sidebarCollapsed} 
          onToggle={() => setSidebarCollapsed(!sidebarCollapsed)}
          onLogout={handleLogout}
          isSuperAdmin={isSuperAdmin}
        />
        <div className="main-content">
          <div className="loading-state">
            <div className="spinner"></div>
            <p>Loading client...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!client) {
    return null;
  }

  const plans = ['Starter', 'Professional', 'Enterprise'];

  return (
    <div className={`dashboard-layout ${sidebarCollapsed ? 'sidebar-collapsed' : ''}`}>
      <Sidebar 
        collapsed={sidebarCollapsed} 
        onToggle={() => setSidebarCollapsed(!sidebarCollapsed)}
        onLogout={handleLogout}
        isSuperAdmin={isSuperAdmin}
      />
      
      <div className="main-content">
        <div className="events-content">
          <div className="events-top">
            <div>
              <h1>Manage Client</h1>
              <p className="subtitle">{client.full_name}</p>
            </div>
            <div style={{display: 'flex', alignItems: 'center', gap: '12px'}}>
              <button 
                className="btn-primary"
                onClick={() => navigate(`/events?clientId=${client.id}&clientName=${encodeURIComponent(client.full_name)}`)}
              >
                + Create Event
              </button>
              <ThemeToggle />
            </div>
          </div>
          {/* Client Details Section */}
          <div style={{
            background: 'var(--card-bg)',
            borderRadius: '12px',
            padding: '24px',
            marginBottom: '24px',
            border: '1px solid var(--border)'
          }}>
            <h2 style={{
              marginBottom: '20px',
              fontSize: '18px',
              fontWeight: '600',
              color: 'var(--text)'
            }}>
              Client Details
            </h2>
            <div style={{display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '20px'}}>
              <div>
                <p style={{fontSize: '13px', color: 'var(--text-secondary)', marginBottom: '6px', fontWeight: '500'}}>Email</p>
                <p style={{fontSize: '15px', fontWeight: '400', color: 'var(--text)'}}>{client.email}</p>
              </div>
              <div>
                <p style={{fontSize: '13px', color: 'var(--text-secondary)', marginBottom: '6px', fontWeight: '500'}}>Phone</p>
                <p style={{fontSize: '15px', fontWeight: '400', color: 'var(--text)'}}>{client.phone || 'N/A'}</p>
              </div>
              <div>
                <p style={{fontSize: '13px', color: 'var(--text-secondary)', marginBottom: '6px', fontWeight: '500'}}>Status</p>
                <span className={`status-badge ${client.subscription_status}`}>
                  {client.subscription_status}
                </span>
              </div>
              <div>
                <p style={{fontSize: '13px', color: 'var(--text-secondary)', marginBottom: '6px', fontWeight: '500'}}>Joined</p>
                <p style={{fontSize: '15px', fontWeight: '400', color: 'var(--text)'}}>
                  {client.created_at ? new Date(client.created_at).toLocaleDateString() : 'N/A'}
                </p>
              </div>
            </div>
          </div>

          {/* Plan Management Section */}
          <div style={{
            background: 'var(--card-bg)',
            borderRadius: '12px',
            padding: '24px',
            marginBottom: '24px',
            border: '1px solid var(--border)'
          }}>
            <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px'}}>
              <h2 style={{fontSize: '18px', fontWeight: '600', color: 'var(--text)', margin: 0}}>
                Subscription Plan
              </h2>
              <button className="btn-primary" onClick={() => setShowPlanModal(true)}>
                Change Plan
              </button>
            </div>
            <div style={{display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '20px'}}>
              <div>
                <p style={{fontSize: '13px', color: 'var(--text-secondary)', marginBottom: '6px', fontWeight: '500'}}>Current Plan</p>
                <p style={{fontSize: '24px', fontWeight: '600', color: '#3B82F6'}}>{client.subscription_tier}</p>
              </div>
              <div>
                <p style={{fontSize: '13px', color: 'var(--text-secondary)', marginBottom: '6px', fontWeight: '500'}}>Payment Amount</p>
                <p style={{fontSize: '24px', fontWeight: '600', color: 'var(--text)'}}>{client.payment_amount || 'N/A'}</p>
              </div>
            </div>
          </div>

          {/* Team Members Section */}
          <div style={{
            background: 'var(--card-bg)',
            borderRadius: '12px',
            padding: '24px',
            border: '1px solid var(--border)'
          }}>
            <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px'}}>
              <h2 style={{fontSize: '18px', fontWeight: '600', color: 'var(--text)', margin: 0}}>
                Team Members ({teamMembers.length})
              </h2>
              <button className="btn-primary" onClick={() => setShowAddModal(true)}>
                <UserPlus size={18} />
                Add Team Member
              </button>
            </div>

            {loadingTeam ? (
              <div className="loading-state">
                <div className="spinner"></div>
                <p>Loading team members...</p>
              </div>
            ) : teamMembers.length === 0 ? (
              <div style={{
                textAlign: 'center',
                padding: '60px 20px',
                color: 'var(--text-secondary)'
              }}>
                <p style={{fontSize: '16px', marginBottom: '8px', color: 'var(--text)'}}>No team members found</p>
                <small style={{fontSize: '14px'}}>Add team members to give them dashboard access</small>
              </div>
            ) : (
              <div className="events-grid">
                {teamMembers.map(member => (
                  <div key={member.id} className="event-card">
                    <div className="user-info">
                      <div className="user-header">
                        <h3>{member.full_name}</h3>
                        <span className={`role-badge ${member.role}`}>
                          {member.role === 'owner' ? (
                            <><Shield size={14} style={{display: 'inline', marginRight: '4px'}} /> Owner</>
                          ) : (
                            <><User size={14} style={{display: 'inline', marginRight: '4px'}} /> Member</>
                          )}
                        </span>
                      </div>
                      <p className="user-detail">📧 {member.email}</p>
                      <p className="user-detail" style={{fontSize: '12px', color: 'var(--text-secondary)'}}>
                        Added {new Date(member.created_at).toLocaleDateString()}
                      </p>
                    </div>
                    {member.role !== 'owner' && (
                      <div className="user-actions">
                        <button 
                          className="btn-delete" 
                          style={{padding: '8px 16px', fontSize: '14px'}} 
                          onClick={() => handleRemoveTeamMember(member.id, member.full_name)}
                        >
                          <Trash2 size={16} />
                          Remove
                        </button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
      
      {/* Add Team Member Modal */}
      {showAddModal && (
        <div className="modal-overlay" onClick={() => setShowAddModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>Add Team Member</h2>
            <p style={{marginBottom: '20px', color: 'var(--text-secondary)'}}>
              Create a new admin account for this client
            </p>
            
            <div className="form-group">
              <label>First Name</label>
              <input
                type="text"
                value={newMemberFirstName}
                onChange={(e) => setNewMemberFirstName(e.target.value)}
                placeholder="John"
              />
            </div>
            
            <div className="form-group">
              <label>Last Name</label>
              <input
                type="text"
                value={newMemberLastName}
                onChange={(e) => setNewMemberLastName(e.target.value)}
                placeholder="Doe"
              />
            </div>
            
            <div className="form-group">
              <label>Email</label>
              <input
                type="email"
                value={newMemberEmail}
                onChange={(e) => setNewMemberEmail(e.target.value)}
                placeholder="john@company.com"
              />
            </div>
            
            <div className="form-group">
              <label>Password</label>
              <input
                type="password"
                value={newMemberPassword}
                onChange={(e) => setNewMemberPassword(e.target.value)}
                placeholder="Min. 8 characters"
              />
            </div>
            
            <div className="form-group">
              <label>Role</label>
              <select value={newMemberRole} onChange={(e) => setNewMemberRole(e.target.value)}>
                <option value="member">Member</option>
                <option value="owner">Owner</option>
              </select>
            </div>
            
            <div className="modal-actions">
              <button 
                className="btn-secondary" 
                onClick={() => setShowAddModal(false)}
                disabled={addingMember}
              >
                Cancel
              </button>
              <button 
                className="btn-primary" 
                onClick={handleAddTeamMember}
                disabled={addingMember}
              >
                {addingMember ? 'Adding...' : 'Add Team Member'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Change Plan Modal */}
      {showPlanModal && (
        <div className="modal-overlay" onClick={() => setShowPlanModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>Change Subscription Plan</h2>
            <p style={{marginBottom: '20px', color: 'var(--text-secondary)'}}>
              Select a new plan for {client.full_name}
            </p>
            
            <div className="form-group">
              <label>Plan</label>
              <select value={selectedPlan} onChange={(e) => setSelectedPlan(e.target.value)}>
                {plans.map(plan => (
                  <option key={plan} value={plan}>{plan}</option>
                ))}
              </select>
            </div>
            
            <div style={{marginBottom: '20px', padding: '12px', backgroundColor: 'var(--hover-bg)', borderRadius: '8px'}}>
              <p style={{fontSize: '14px', color: 'var(--text-secondary)', margin: 0}}>
                Current plan: <strong>{client.subscription_tier}</strong> → New plan: <strong>{selectedPlan}</strong>
              </p>
            </div>
            
            <div className="modal-actions">
              <button 
                className="btn-secondary" 
                onClick={() => setShowPlanModal(false)}
                disabled={updatingPlan}
              >
                Cancel
              </button>
              <button 
                className="btn-primary" 
                onClick={handleUpdatePlan}
                disabled={updatingPlan}
              >
                {updatingPlan ? 'Updating...' : 'Update Plan'}
              </button>
            </div>
          </div>
        </div>
       )}
      
    </div>
  );
}

export default ManageClient;
